import { useState, useEffect, useRef, useCallback } from 'react'
import type { Station, StationWithDistance, UserLocation } from '@fuelfinder/shared'
import { getAllTileGeohashes, calculateDistanceMiles } from '@fuelfinder/shared'
import { fetchAllTiles } from '../lib/api'

/**
 * Cache entry for fetched tiles
 */
interface TileCache {
  stations: Station[]
  fetchedAt: number
  userLat: number
  userLng: number
}

/**
 * Cache stale time in milliseconds (5 minutes)
 */
const CACHE_STALE_TIME = 5 * 60 * 1000

/**
 * Minimum distance in miles before refetching (500m = ~0.31 miles)
 */
const MIN_REFETCH_DISTANCE = 0.31

interface UseTilesReturn {
  stations: StationWithDistance[]
  isLoading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Hook for fetching tile-based station data
 * - Computes 9 tile geohashes (center + 8 neighbors) based on user location
 * - Fetches tiles in parallel and merges/dedupes stations
 * - Adds distance from user location to each station
 * - Caches tiles with 5-min stale time
 * - Only refetches if user moved >500m
 */
export function useTiles(userLocation: UserLocation | null): UseTilesReturn {
  const [stations, setStations] = useState<StationWithDistance[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Cache reference
  const cacheRef = useRef<TileCache | null>(null)

  // Fetch trigger for manual refetch
  const [fetchTrigger, setFetchTrigger] = useState(0)

  const refetch = useCallback(() => {
    // Clear cache to force refetch
    cacheRef.current = null
    setFetchTrigger((prev) => prev + 1)
  }, [])

  useEffect(() => {
    // Skip if no location
    if (!userLocation) {
      setStations([])
      return
    }

    const { lat, lng } = userLocation

    // Check if we can use cached data
    const cache = cacheRef.current
    if (cache) {
      const isFresh = Date.now() - cache.fetchedAt < CACHE_STALE_TIME
      const distance = calculateDistanceMiles(lat, lng, cache.userLat, cache.userLng)
      const userMovedEnough = distance > MIN_REFETCH_DISTANCE

      if (isFresh && !userMovedEnough) {
        // Recalculate distances with new user location but use cached stations
        const updatedStations = cache.stations.map((station) => ({
          ...station,
          distanceMiles: calculateDistanceMiles(lat, lng, station.lat, station.lng),
        }))
        setStations(updatedStations.sort((a, b) => a.distanceMiles - b.distanceMiles))
        return
      }
    }

    // Fetch tiles
    let cancelled = false

    async function loadTiles() {
      setIsLoading(true)
      setError(null)

      try {
        // Get the 9 tile geohashes for this location
        const geohashes = getAllTileGeohashes(lat, lng)

        // Fetch all tiles and merge stations
        const fetchedStations = await fetchAllTiles(geohashes)

        if (cancelled) return

        // Add distance and sort
        const stationsWithDistance: StationWithDistance[] = fetchedStations
          .map((station) => ({
            ...station,
            distanceMiles: calculateDistanceMiles(lat, lng, station.lat, station.lng),
          }))
          .sort((a, b) => a.distanceMiles - b.distanceMiles)

        // Update cache
        cacheRef.current = {
          stations: fetchedStations,
          fetchedAt: Date.now(),
          userLat: lat,
          userLng: lng,
        }

        setStations(stationsWithDistance)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to fetch stations')
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadTiles()

    return () => {
      cancelled = true
    }
  }, [userLocation, fetchTrigger])

  return {
    stations,
    isLoading,
    error,
    refetch,
  }
}
